import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Study } from '@models';
import { CommonFunctionsService, StudyService } from '@services';
import {
  columnConfigurations,
  getMilestoneTooltipDeltaParts as getMilestoneTooltipDeltaPartsUtil,
  getMilestoneTooltipTitle as getMilestoneTooltipTitleUtil,
  shouldShowMilestoneTooltip as shouldShowMilestoneTooltipUtil
} from '@utils';

@Component({
  selector: 'portfolio-table',
  templateUrl: './portfolio-table.component.html',
  styleUrl: './portfolio-table.component.scss'
})
export class PortfolioTableComponent implements OnInit, OnDestroy {
  private static readonly HIDE_TOOLTIP_DELAY_MS = 150;

  @Input() dataSource = new MatTableDataSource<Study>([]);
  @Input() hasData = true;
  @Input() routePrefix = '/portfolio';
  @Input() enableSticky = true;
  @Input() enableHeaderMenu = false;
  @Input() enableSort = false;

  private studyService = inject(StudyService);
  private commonFunctionsService = inject(CommonFunctionsService);

  displayedColumns: (keyof Study)[] = [];
  columnConfigurations = columnConfigurations;
  allColumns: (keyof Study)[] = Object.keys(columnConfigurations) as (keyof Study)[];
  countryTooltipElement: Study | null = null;
  ppdRegionTooltipElement: Study | null = null;
  protocolTooltipOpen = false;
  protocolTooltipElement: Study | null = null;
  milestoneTooltipOpen = false;
  milestoneTooltipElement: Study | null = null;
  readonly shouldShowMilestoneTooltip = shouldShowMilestoneTooltipUtil;
  readonly getMilestoneTooltipTitle = getMilestoneTooltipTitleUtil;
  readonly getMilestoneTooltipDeltaParts = getMilestoneTooltipDeltaPartsUtil;
  currentSortColumn: string | null = null;
  currentSortDirection: 'asc' | 'desc' | null = null;
  private protocolTooltipHideTimeoutId: number | null = null;
  private isProtocolAnchorHovered = false;
  private isProtocolTooltipHovered = false;
  private milestoneTooltipHideTimeoutId: number | null = null;
  private isMilestoneAnchorHovered = false;
  private isMilestoneTooltipHovered = false;

  onProtocolMouseEnter(element: Study): void {
    this.isProtocolAnchorHovered = true;

    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    this.protocolTooltipElement = element;
    this.protocolTooltipOpen = true;
  }

  onProtocolMouseLeave(): void {
    this.isProtocolAnchorHovered = false;
    this.protocolTooltipHide();
  }

  onProtocolTooltipHover(): void {
    this.isProtocolTooltipHovered = true;

    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }
  }

  onProtocolTooltipLeave(): void {
    this.isProtocolTooltipHovered = false;
    this.protocolTooltipHide();
  }

  private protocolTooltipHide(): void {
    if (this.protocolTooltipHideTimeoutId) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    if (this.isProtocolAnchorHovered || this.isProtocolTooltipHovered) {
      return;
    }

    this.protocolTooltipHideTimeoutId = window.setTimeout(() => {
      this.protocolTooltipOpen = false;
      this.protocolTooltipHideTimeoutId = null;
    }, PortfolioTableComponent.HIDE_TOOLTIP_DELAY_MS);
  }

  onMilestoneMouseEnter(element: Study): void {
    if (!this.shouldShowMilestoneTooltip(element)) {
      return;
    }

    this.isMilestoneAnchorHovered = true;

    if (this.milestoneTooltipHideTimeoutId) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
      this.milestoneTooltipHideTimeoutId = null;
    }

    this.milestoneTooltipElement = element;
    this.milestoneTooltipOpen = true;
  }

  onMilestoneMouseLeave(): void {
    this.isMilestoneAnchorHovered = false;
    this.milestoneTooltipHide();
  }

  onMilestoneTooltipHover(): void {
    this.isMilestoneTooltipHovered = true;

    if (this.milestoneTooltipHideTimeoutId) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
      this.milestoneTooltipHideTimeoutId = null;
    }
  }

  onMilestoneTooltipLeave(): void {
    this.isMilestoneTooltipHovered = false;
    this.milestoneTooltipHide();
  }

  getMilestoneTooltipDate(value: string | null | undefined): string {
    return this.commonFunctionsService.formatDate(value ?? null);
  }

  private milestoneTooltipHide(): void {
    if (this.milestoneTooltipHideTimeoutId) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
      this.milestoneTooltipHideTimeoutId = null;
    }

    if (this.isMilestoneAnchorHovered || this.isMilestoneTooltipHovered) {
      return;
    }

    this.milestoneTooltipHideTimeoutId = window.setTimeout(() => {
      this.milestoneTooltipOpen = false;
      this.milestoneTooltipHideTimeoutId = null;
    }, PortfolioTableComponent.HIDE_TOOLTIP_DELAY_MS);
  }

  toggleCountryTooltip(element: Study): void {
    this.countryTooltipElement = this.countryTooltipElement === element ? null : element;
  }

  onCountryTooltipChange(isOpen: boolean, element: Study): void {
    this.countryTooltipElement = isOpen ? element : null;
  }

  togglePpdRegionTooltip(element: Study): void {
    this.ppdRegionTooltipElement = this.ppdRegionTooltipElement === element ? null : element;
  }

  onPpdRegionTooltipChange(isOpen: boolean, element: Study): void {
    this.ppdRegionTooltipElement = isOpen ? element : null;
  }
  updateDisplayedColumns(): void {
    const clientNamesList = this.studyService.getParentClientNames();
    if (clientNamesList.length > 1) {
      this.displayedColumns = [...this.allColumns];
    } else {
      this.displayedColumns = this.allColumns.filter((_, index) => index !== 0 && index !== 2);
    }
  }
  ngOnInit(): void {
    this.updateDisplayedColumns();
  }

  ngOnDestroy(): void {
    if (this.protocolTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.protocolTooltipHideTimeoutId);
      this.protocolTooltipHideTimeoutId = null;
    }

    if (this.milestoneTooltipHideTimeoutId !== null) {
      window.clearTimeout(this.milestoneTooltipHideTimeoutId);
      this.milestoneTooltipHideTimeoutId = null;
    }
  }
}
